import { useOutletContext, useNavigate } from "react-router-dom";
import Topbar from "@/components/Topbar";
import { POLE_LABELS } from "@/lib/api";
import { ArrowRight, Warning } from "@phosphor-icons/react";

export default function Dashboard() {
  const { stats } = useOutletContext();
  const navigate = useNavigate();

  const totals = stats.reduce(
    (acc, s) => ({
      unassigned: acc.unassigned + s.unassigned,
      processing: acc.processing + s.processing,
      completed: acc.completed + s.completed,
      archived: acc.archived + s.archived,
    }),
    { unassigned: 0, processing: 0, completed: 0, archived: 0 }
  );

  return (
    <>
      <Topbar
        title="Tableau de bord"
        subtitle="Vue d'ensemble des pôles · Statuts et flux de demandes"
      />

      <main className="px-4 sm:px-6 lg:px-8 py-5 sm:py-8 max-w-[1600px] w-full" data-testid="dashboard">
        {/* KPIs */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-zinc-200 bg-white mb-8" data-testid="global-kpis">
          <KPI label="Non pris en charge" value={totals.unassigned} dot="bg-red-600" testId="kpi-unassigned" />
          <KPI label="En cours" value={totals.processing} dot="bg-orange-500" testId="kpi-processing" />
          <KPI label="Traités" value={totals.completed} dot="bg-green-600" testId="kpi-completed" />
          <KPI label="Archivés" value={totals.archived} dot="bg-zinc-500" testId="kpi-archived" last />
        </section>

        {totals.unassigned > 0 && (
          <div className="border border-red-200 bg-red-50 px-5 py-3 flex items-center gap-3 mb-6" data-testid="alert-unassigned">
            <Warning size={20} weight="fill" className="text-red-600 shrink-0" />
            <div className="text-sm text-red-800">
              <span className="font-semibold">{totals.unassigned} demande{totals.unassigned > 1 ? "s" : ""}</span> en attente de prise en charge — action requise.
            </div>
          </div>
        )}

        {/* Pole grid */}
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold tracking-tight">Pôles de l'entreprise</h2>
          <span className="label-tiny text-zinc-400">{stats.length} pôles actifs</span>
        </div>
        <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
          {stats.map((s) => (
            <PoleCard
              key={s.id}
              data={s}
              onClick={() => navigate(`/pole/${s.id}`)}
            />
          ))}
        </section>
      </main>
    </>
  );
}

function KPI({ label, value, dot, testId, last }) {
  return (
    <div className={`px-6 py-5 ${!last ? "border-r border-zinc-200" : ""}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className={`w-2 h-2 rounded-full ${dot}`} />
        <span className="label-tiny text-zinc-500">{label}</span>
      </div>
      <div className="font-heading text-3xl font-semibold tabular-nums" data-testid={testId}>
        {value}
      </div>
    </div>
  );
}

function PoleCard({ data, onClick }) {
  const { id, name, unassigned, processing, completed, archived } = data;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`pole-card-${id}`}
      className="group text-left bg-white border border-zinc-200 hover:border-zinc-900 hover:shadow-sm transition-all duration-150 p-5"
    >
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="label-tiny text-zinc-400 mb-1">Pôle</div>
          <h3 className="font-heading text-lg font-semibold tracking-tight">{name}</h3>
          <div className="font-mono text-[11px] text-zinc-400 mt-1">/{id}</div>
        </div>
        <ArrowRight
          size={18}
          weight="bold"
          className="text-zinc-300 group-hover:text-zinc-900 group-hover:translate-x-0.5 transition-all"
        />
      </div>

      <div className="grid grid-cols-3 gap-0 border border-zinc-200">
        <Counter color="bg-red-100 text-red-800" dot="bg-red-600" value={unassigned} label="Non pris" testId={`pole-${id}-red`} pulse={unassigned > 0} />
        <Counter color="bg-orange-100 text-orange-800" dot="bg-orange-500" value={processing} label="En cours" testId={`pole-${id}-orange`} border />
        <Counter color="bg-green-100 text-green-800" dot="bg-green-600" value={completed} label="Traités" testId={`pole-${id}-green`} />
      </div>

      <div className="mt-3 flex items-center justify-between text-xs text-zinc-500">
        <span>Archivés <span className="font-mono font-semibold text-zinc-700 ml-1">{archived}</span></span>
        <span className="font-mono">{unassigned + processing + completed + archived} total</span>
      </div>
    </button>
  );
}

function Counter({ color, dot, value, label, testId, border, pulse }) {
  return (
    <div className={`px-3 py-3 ${border ? "border-x" : ""} border-zinc-200`}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className={`w-1.5 h-1.5 rounded-full ${dot} ${pulse ? "pulse-red" : ""}`} />
        <span className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">{label}</span>
      </div>
      <div className={`font-heading text-2xl font-semibold tabular-nums ${value > 0 ? color.split(" ")[1] : "text-zinc-300"}`} data-testid={testId}>
        {value}
      </div>
    </div>
  );
}
